import type { IMarketGroupChanceHistory } from "~/feature/chart/types";
import type { IMarket } from "~/types/event";
import { chanceColumn, isUntraded, readMarketValue } from "~/utils/marketValue";

type ChancePoint = IMarketGroupChanceHistory["historical"][number];

interface IDropUnavailableMarketsOptions {
  keepConsensus?: boolean;
}

// The rule the market table blanks a % Chance cell by (PercentageChangeField).
export const hasAvailableChance = (market: IMarket): boolean =>
  !isUntraded(market.volume) && readMarketValue(chanceColumn, market) !== null;

const rebuildPoint = (
  point: ChancePoint,
  unavailable: Set<string>,
  keepConsensus: boolean
): ChancePoint | null => {
  const byPlatform = point.byPlatform.filter(
    ({ marketId }) => !unavailable.has(marketId)
  );

  if (!byPlatform.length && !(keepConsensus && point.consensus)) {
    return null;
  }

  if (byPlatform.length === point.byPlatform.length) {
    return point;
  }

  return { ...point, byPlatform };
};

const isPoint = (point: ChancePoint | null): point is ChancePoint =>
  point !== null;

export const dropUnavailableMarkets = (
  chanceHistory: IMarketGroupChanceHistory[],
  markets: IMarket[],
  { keepConsensus = false }: IDropUnavailableMarketsOptions = {}
): IMarketGroupChanceHistory[] => {
  const unavailable = new Set(
    markets.filter((market) => !hasAvailableChance(market)).map(({ id }) => id)
  );

  if (!unavailable.size) {
    return chanceHistory;
  }

  return chanceHistory.map((group) => ({
    ...group,
    historical: group.historical
      .map((point) => rebuildPoint(point, unavailable, keepConsensus))
      .filter(isPoint),
  }));
};
